import { useTranslation } from "react-i18next";
import useCredentials from "../../common/hooks/credentialsHook";
import useNetwork from "../../common/hooks/networkHook";
import { addContactListMapping, canCreateList, canRemoveList, canUpdateList, createContactList, removeContactList, removeContactListMapping, updateContactList, useContactLists, useContacts, useListContacts } from "../../reducers/contactSlice";
import { useMemo, useState } from "react";
import { SettingsDisplay } from "../../common/components/SettingsPanels";
import DataTable from "../../common/components/DataTable";
import Button from "../../common/components/Button";
import Icon from "../../common/components/Icon";
import { ReactNodeDialog } from "../../common/utils/dialogUtlities";
import PropTypes from "prop-types";

const defaultSort = ['name'];

const ContactListMembers = ({ listid, onClose }) => {
    const { t } = useTranslation();
    const network = useNetwork();
    const contacts = useListContacts(listid);
    const allContacts = useContacts();

    const emailMembers = useMemo(() => contacts.filter(cl => cl.type === 'Email'), [contacts]);
    const mobileMembers = useMemo(() => contacts.filter(cl => cl.type === 'Mobile'), [contacts]);

    const emailTable = useMemo(() => [
        { type: "text", field: "name", title: t('name'), readonly: true },
        { type: "email", readonly: true, field: "email", title: t('email') },
    ], [t]);

    const emailFooterTable = useMemo(() => [
        { type: "option", field: "id", title: t('name'), key: 'name', value: 'id', options: allContacts.filter(c => c.email && emailMembers.every(m => m.id !== c.id)), required: true },
        { type: "label", text: " ", uuid: "emailFooterSpacer"},
    ], [t, allContacts, emailMembers]);

    const mobileTable = useMemo(() => [
        { type: "text", field: "name", title: t('name'), readonly: true },
        { type: "phone", readonly: true, field: "mobile", title: t('mobile') }
    ], [t]);

    const mobileFooterTable = useMemo(() => [
        { type: "option", field: "id", title: t('name'), key: 'name', value: 'id', options: allContacts.filter(c => c.mobile && mobileMembers.every(m => m.id !== c.id)), required: true },
        { type: "label", text: " ", uuid: "mobileFooterSpacer", }
    ], [t, allContacts, mobileMembers]);

    return <ReactNodeDialog showModal={!!listid} onClose={onClose} title={t('contactList_other')} style={{ overflowY: 'visible'}} size="lg">
        <div className="d-flex flex-column">
                <h4>Email Contacts</h4>
                <DataTable value={emailMembers} columns={emailTable} footerColumns={emailFooterTable} style={{ flex: 1, maxHeight: 'calc(50vh - 140px)' }}
                    showFilter={true}
                    onRemove={c => removeContactListMapping(network, c.id, listid, 'Email')}
                    onAdd={c => addContactListMapping(network, c.id, listid, 'Email')}></DataTable>

                <h4 className="mt-2">Mobile Contacts</h4>
                <DataTable value={mobileMembers} columns={mobileTable} footerColumns={mobileFooterTable} style={{ flex: 1, maxHeight: 'calc(50vh - 120px)' }}
                    showFilter={true}
                    onRemove={c => removeContactListMapping(network, c.id, listid, 'Mobile')}
                    onAdd={c => addContactListMapping(network, c.id, listid, 'Mobile')}></DataTable>
        </div>
    </ReactNodeDialog>
}

const ContactLists = ({ uuid, label, onClose }) => {
    const { t } = useTranslation();
    const credentials = useCredentials();
    const network = useNetwork();
    const contactLists = useContactLists();
    const [selectedList, setSelectedList] = useState();

    const tableDef = useMemo(() => [
        { type: "text", editable: true, field: "name", title: t('name') },
        { type: "option", editable: true, field: "accessibility", title: t('accessibilityPermissions'), key: "label", value: "value", wildcard: "Restricted", options: [{ label: "Dispatch", value: "Dispatch" },{ label: "Crew", value: "Crew" },{ label: "All", value: "All" }] },
        { type: "checkbox", editable: true, field: "to", title: t('toRecipients') },
        { type: "checkbox", editable: true, field: "cc", title: t('ccRecipients') },
        { type: "checkbox", editable: true, field: "bcc", title: t('bccRecipients') },
        { type: "dynamic", editable: false, readonly: true, title: "", def: (value, disabled, hideLabel, onChange) => value.id && <Button variant="default" onClick={() => setSelectedList(value.id)}><Icon intent="view"></Icon> {t('view')}</Button>}
    ], [t]);

    return <>
        <ContactListMembers listid={selectedList} onClose={() => setSelectedList()}></ContactListMembers>
        <SettingsDisplay uuid={uuid} title={label} onClose={onClose}>
            <DataTable value={contactLists} columns={tableDef} defaultSort={defaultSort}
                canAdd={list => canCreateList(credentials, list)}
                canUpdate={list => canUpdateList(credentials, list)}
                canRemove={list => canRemoveList(credentials, list)}
                showFilter={true}
                onAdd={list => createContactList(network, list)}
                onUpdate={(list, field, value) => updateContactList(network, { id: list.id, [field]: value })}
                onRemove={list => removeContactList(network, list)}
            >
            </DataTable>
        </SettingsDisplay>
    </>
}
ContactLists.propTypes = {
    /**
     * ID to identify the settings status entry
     */
    uuid: PropTypes.string.isRequired,
    /**
     * Label for the screen
     */
    label: PropTypes.string.isRequired,
    /**
     * Function to call when the screen is closed
     */
    onClose: PropTypes.func
}

export default ContactLists;